import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Wrench, ShieldCheck, Lightbulb, MapPin,
  Users, Clock, BadgeCheck, Layers, HardDrive, Server, ScanSearch,
  Wifi, Camera, Code2, Gauge, Handshake, Cpu,
} from "lucide-react";
import { Eyebrow, SectionHeading } from "@/components/Section";
import { SEO } from "@/components/SEO";

const SITE_URL = "https://it-servicepoint.eu";

const PILLARS = [
  {
    icon: Wrench,
    title: "IT-Service & Support",
    desc: "Wir halten Ihre IT am Laufen: Anwender-Support, Wartung, Updates, Monitoring und schnelle Hilfe bei Störungen – remote und vor Ort. Ein Anruf genügt, wir kümmern uns.",
    points: ["Reaktionszeit unter 40 Minuten (SLA)", "Remote- & Vor-Ort-Service", "Wartung, Patches & Monitoring"],
  },
  {
    icon: ShieldCheck,
    title: "IT-Sicherheit als Managed Service",
    desc: "Als zertifizierter Securepoint- und Greenbone-Partner betreiben wir Firewall, Antivirus, XDR, Backup und Schwachstellenmanagement für Sie – 24/7, made in Germany.",
    points: ["24/7 SOC-Monitoring", "NIS-2- & DSGVO-konform", "Rechenzentren nur in Deutschland"],
  },
  {
    icon: Lightbulb,
    title: "IT-Beratung mit Sachverstand",
    desc: "Als IT-Berater analysieren wir Ihre Infrastruktur, decken Risiken und Einsparpotenziale auf und begleiten Digitalisierung, Cloud-Umzug und Compliance-Projekte.",
    points: ["IT-Bestandsaufnahme & Strategie", "Herstellerneutrale Empfehlungen", "Festpreis-Angebote statt Überraschungen"],
  },
];

const SERVICES = [
  { icon: Cpu, title: "Lokale KI", href: "/lokale-ki" },
  { icon: Layers, title: "Managed Services", href: "/managed-services" },
  { icon: ShieldCheck, title: "Managed Security", href: "/managed-security" },
  { icon: HardDrive, title: "Backup & Recovery", href: "/managed-backup" },
  { icon: Server, title: "Server Hosting", href: "/managed-server" },
  { icon: ScanSearch, title: "Schwachstellenmanagement", href: "/schwachstellenmanagement" },
  { icon: Wifi, title: "Managed WLAN", href: "/managed-wlan" },
  { icon: Camera, title: "Überwachungskameras", href: "/ueberwachungskameras" },
  { icon: Code2, title: "Webdesign & SEO", href: "/webdesign" },
  { icon: Gauge, title: "Webseiten-Check", href: "/webseiten-check" },
];

const CITIES = ["Fulda", "Bad Hersfeld", "Hünfeld", "Kassel", "Erfurt", "Würzburg", "Schenklengsfeld", "Rotenburg a. d. Fulda"];

const WHY = [
  { icon: Users, title: "Inhabergeführt & persönlich", desc: "Sie sprechen mit Menschen, die Ihre Umgebung kennen – kein Ticket-Ping-Pong, kein Callcenter." },
  { icon: Clock, title: "Über 10 Jahre Erfahrung", desc: "Seit 2014 betreuen wir kleine und mittelständische Unternehmen in Hessen und darüber hinaus." },
  { icon: BadgeCheck, title: "Zertifizierte Partner", desc: "Securepoint Partner, Greenbone Certified, Acronis Backup – Prozesse nach ISO-27001-Methodik." },
  { icon: Handshake, title: "Faire, planbare Kosten", desc: "Monatliche Pauschalen statt Stunden-Lotterie. Festpreis-Angebote in 3 Werktagen. 0 € versteckte Kosten." },
];

const FAQ = [
  { q: "Was macht ein IT-Dienstleister für kleine und mittlere Unternehmen?", a: "Ein IT-Dienstleister übernimmt Aufbau, Betrieb und Absicherung Ihrer gesamten IT: von Arbeitsplätzen, Servern und Netzwerk über E-Mail und Backup bis zur IT-Sicherheit. Bei IT-Servicepoint bekommen Sie das als planbaren Managed Service – inklusive Monitoring, Wartung, Support und einem festen Ansprechpartner. So brauchen Sie keine eigene IT-Abteilung." },
  { q: "Was kostet IT-Service für ein KMU?", a: "Das hängt von Anzahl der Arbeitsplätze, Servern und gewünschtem Leistungsumfang ab. Unsere Managed Services werden pro Monat und Gerät/Nutzer abgerechnet – transparent und planbar, ohne versteckte Kosten. Nach einem kostenfreien 30-Minuten-Erstgespräch erhalten Sie innerhalb von 3 Werktagen ein verbindliches Festpreis-Angebot." },
  { q: "Ab wie vielen Mitarbeitern lohnt sich ein externer IT-Dienstleister?", a: "Bereits ab etwa 5 Arbeitsplätzen rechnet sich ein Managed-Service-Modell: Ausfälle, Sicherheitsvorfälle und Update-Chaos kosten schnell mehr als eine professionelle Betreuung. Nach oben betreuen wir Unternehmen bis mehrere hundert Mitarbeiter." },
  { q: "Bieten Sie IT-Beratung auch als einmaliges Projekt an?", a: "Ja. Als IT-Berater führen wir auch einmalige IT-Bestandsaufnahmen, Sicherheits-Audits, NIS-2-Readiness-Checks oder Digitalisierungs-Beratungen durch – mit konkretem Maßnahmenplan als Ergebnis. Viele Kunden starten mit einem Beratungsprojekt und entscheiden sich danach für die laufende Betreuung." },
  { q: "Wie schnell reagieren Sie im Störungsfall?", a: "Vertraglich zugesichert: unter 40 Minuten Reaktionszeit, 7 Tage die Woche. Kritische Sicherheitsvorfälle behandelt unser 24/7-Monitoring automatisch – oft bevor Sie die Störung überhaupt bemerken." },
  { q: "In welchen Regionen sind Sie als IT-Dienstleister tätig?", a: "Unser Standort liegt in Schenklengsfeld bei Bad Hersfeld. Vor-Ort-Service leisten wir in Osthessen (Fulda, Bad Hersfeld, Hünfeld), Nordhessen (Kassel), Thüringen (Erfurt) und Unterfranken (Würzburg). Alle Managed Services und die IT-Beratung sind bundesweit verfügbar, da Monitoring und Support remote erfolgen." },
];

export default function ItDienstleister() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": `${SITE_URL}/it-dienstleister#service`,
      name: "IT-Dienstleister, IT-Service & IT-Beratung",
      alternateName: ["IT Service", "IT Dienstleister", "IT Berater", "IT-Beratung", "IT-Systemhaus"],
      provider: { "@id": `${SITE_URL}/#organization` },
      serviceType: "Managed IT services, IT support and IT consulting",
      description:
        "IT-Servicepoint ist Ihr inhabergeführter IT-Dienstleister in Hessen: IT-Service mit SLA, Managed Security, Backup, Server Hosting und IT-Beratung für kleine und mittelständische Unternehmen in Fulda, Bad Hersfeld, Hünfeld, Kassel, Erfurt und Würzburg.",
      areaServed: CITIES.map((c) => ({ "@type": "City", name: c })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "IT-Dienstleister", item: `${SITE_URL}/it-dienstleister` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div data-testid="it-dienstleister-page" className="relative">
      <SEO
        title="IT-Dienstleister Hessen · IT-Service & IT-Berater für den Mittelstand"
        description="Ihr IT-Dienstleister in Hessen: IT-Service mit garantierter Reaktionszeit, Managed Security, Backup & IT-Beratung für KMU in Fulda, Bad Hersfeld, Kassel, Erfurt & Würzburg. Inhabergeführt, zertifiziert, made in Germany – kostenfreies Erstgespräch."
        keywords="IT-Dienstleister, IT Service, IT Berater, IT-Beratung, IT-Dienstleister Hessen, IT Service Fulda, IT Dienstleister Bad Hersfeld, IT Berater Kassel, IT Systemhaus, IT Support Mittelstand, Managed IT Services, IT Betreuung KMU, externe IT-Abteilung, IT Outsourcing"
        jsonLd={jsonLd}
        type="service"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-soft border-b border-line">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <Eyebrow>IT-Dienstleister · IT-Service · IT-Beratung</Eyebrow>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-ink">
              Ihr IT-Dienstleister<br />
              für den <span className="text-primary">Mittelstand</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-muted leading-relaxed">
              IT-Service, der einfach läuft. Sicherheit, die hält. Und ein IT-Berater, der Ihre Sprache spricht – nicht Fach-Chinesisch. <strong className="text-ink">IT-Servicepoint</strong> ist die externe IT-Abteilung für Unternehmen in Hessen, Thüringen und Unterfranken. Inhabergeführt, zertifiziert, seit über 10 Jahren.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                data-testid="itd-hero-cta"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white font-medium hover:bg-primary-hover transition-colors shadow-soft"
              >
                Kostenfreies Erstgespräch
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+4966299153890"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-line bg-white text-ink hover:border-primary hover:text-primary transition-colors font-medium"
              >
                +49 (0) 6629 9153890
              </a>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="border border-line bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-primary mb-4">
                <MapPin size={14} /> IT-Service vor Ort in
              </div>
              <div className="flex flex-wrap gap-2">
                {CITIES.map((c) => (
                  <span key={c} className="border border-line bg-soft px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink">{c}</span>
                ))}
              </div>
              <p className="mt-4 text-muted text-xs leading-relaxed">Managed Services & IT-Beratung bundesweit – Monitoring und Support erfolgen remote.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="Drei Rollen, ein Partner"
            title="IT-Service. IT-Sicherheit. IT-Beratung."
            sub="Die meisten Unternehmen brauchen keinen weiteren Lieferanten, sondern einen IT-Dienstleister, der Verantwortung übernimmt. Genau das tun wir – in drei Disziplinen."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="border border-line bg-white p-8 lift" data-testid={`itd-pillar-${i}`}>
                <div className="w-12 h-12 bg-soft border border-line flex items-center justify-center text-primary mb-6">
                  <p.icon size={22} />
                </div>
                <h3 className="font-display font-bold text-xl text-ink">{p.title}</h3>
                <p className="mt-4 text-muted text-sm leading-relaxed">{p.desc}</p>
                <ul className="mt-6 space-y-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm text-ink">
                      <CheckCircle2 size={15} className="text-accent mt-0.5 shrink-0" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES LINK GRID */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="Unser Leistungsspektrum"
            title="Alles, was Ihre IT braucht – aus einer Hand."
          />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-px bg-line border border-line">
            {SERVICES.map((s, i) => (
              <Link
                key={s.href}
                to={s.href}
                data-testid={`itd-service-${i}`}
                className="group bg-white p-6 sm:p-8 hover:bg-soft transition-colors flex items-center gap-4"
              >
                <div className="w-10 h-10 shrink-0 bg-soft border border-line flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                  <s.icon size={18} />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-ink group-hover:text-primary transition-colors leading-tight">{s.title}</div>
                </div>
                <ArrowRight size={16} className="text-muted group-hover:text-primary transition-all group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="Warum IT-Servicepoint?"
            title="Ein IT-Berater, der bleibt, wenn's brennt."
            sub="Wir sind kein anonymes Systemhaus mit wechselnden Technikern. Wir sind Ihr fester Ansprechpartner – mit Handschlagqualität und vertraglich zugesicherten Reaktionszeiten."
          />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => (
              <div key={w.title} className="border border-line bg-white p-8 lift" data-testid={`itd-why-${i}`}>
                <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary mb-5">
                  <w.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg text-ink leading-tight">{w.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO TEXT */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <Eyebrow>Hintergrund</Eyebrow>
          <div className="mt-6 space-y-6 text-muted text-lg leading-relaxed">
            <p>
              <strong className="text-ink">IT-Servicepoint ist Ihr IT-Dienstleister in Hessen</strong> – mit Sitz in Schenklengsfeld bei Bad Hersfeld, mitten zwischen Fulda, Kassel, Erfurt und Würzburg. Seit 2014 betreuen wir kleine und mittelständische Unternehmen als externe IT-Abteilung: vom einzelnen Arbeitsplatz über Server und Netzwerk bis zur kompletten Sicherheits-Infrastruktur.
            </p>
            <p>
              Unser <strong className="text-ink">IT-Service</strong> ist als Managed Service organisiert: Statt stundenweiser Abrechnung erhalten Sie planbare Monatspauschalen, proaktives Monitoring und garantierte Reaktionszeiten unter 40 Minuten. Störungen beheben wir häufig, bevor sie im Tagesgeschäft auffallen.
            </p>
            <p>
              Als <strong className="text-ink">IT-Berater</strong> unterstützen wir Sie bei strategischen Entscheidungen: IT-Bestandsaufnahme, Sicherheits-Audit, NIS-2-Readiness, Cloud- oder Server-Strategie und Digitalisierungsprojekte – herstellerneutral und mit einem konkreten Maßnahmenplan als Ergebnis. IT made in Germany: Alle Rechenzentren und Analysten sitzen ausschließlich in Deutschland und Europa.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-b border-line">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <Eyebrow>Häufige Fragen</Eyebrow>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-5xl tracking-tightest text-ink">FAQ.</h2>
          <div className="mt-12 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="border border-line bg-white p-6 group" data-testid={`itd-faq-${i}`}>
                <summary className="font-display font-bold text-lg text-ink cursor-pointer flex items-center justify-between gap-4">
                  {f.q}
                  <span className="text-primary group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-4 text-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 text-center">
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tightest leading-[1.05] max-w-3xl mx-auto">
            Suchen Sie einen IT-Dienstleister, der liefert?
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 text-lg">
            30 Minuten Erstgespräch · kostenfrei · unverbindlich. Wir hören zu, prüfen Ihre IT und sagen Ihnen ehrlich, was sich lohnt – und was nicht.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="itd-cta-contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
            >
              Termin vereinbaren <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+4966299153890"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors"
            >
              +49 (0) 6629 9153890
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
